/**
 * useNotifications – Sprint 6 refactor
 * Uses notificationService (DAL) with Realtime subscription
 */
import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '@/services';
import type { NotificationRow } from '@/services';
import { supabase } from '@/core/database/supabase';

export type NotificationItem = NotificationRow;

export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setIsLoading(false); return; }

    const result = await notificationService.list(user.id, { pageSize: 50 });
    if (!result.error) {
      setNotifications(result.data);
      setUnreadCount(result.data.filter(n => n.status_code === 'unread').length);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchNotifications();

    let channelCleanup: (() => void) | undefined;

    const setupRealtime = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const channel = notificationService.subscribeToNotifications(user.id, (newItem) => {
        setNotifications(prev => [newItem, ...prev]);
        setUnreadCount(prev => prev + 1);
      });

      channelCleanup = () => notificationService.unsubscribe(channel);
    };

    setupRealtime();

    return () => { channelCleanup?.(); };
  }, [fetchNotifications]);

  const markAllRead = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await notificationService.markAllRead(user.id);
    setNotifications(prev => prev.map(n => ({ ...n, status_code: 'read', read_at: n.read_at ?? new Date().toISOString() })));
    setUnreadCount(0);
  }, []);

  const markRead = useCallback(async (id: string) => {
    await notificationService.markRead(id);
    setNotifications(prev => prev.map(n =>
      n.id === id ? { ...n, status_code: 'read', read_at: new Date().toISOString() } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  return { notifications, isLoading, unreadCount, markAllRead, markRead, refetch: fetchNotifications };
}
